// src/lib/producerLabel.ts
//
// The public Producer label: the member's own statement (a self-declared producer account, free
// in beta). It is not an ID check and is never merged with, styled like, or placed inside the
// Verified badge. The api decides it (People.IsProducer on, an approved profile that is not held
// or suspended, and not turned off by staff) and sends the role; the ui only reads the fields.
//
// The role is the member's answer to "How should your profile show?": "producer" (producer only,
// in the producers list and not in talent search) or "producer_and_talent" (in both). The badge
// reads "Producer" or "Producer and talent". There is no capability difference between them.

export const PRODUCER_LABEL = 'Producer';
export const PRODUCER_AND_TALENT_LABEL = 'Producer and talent';

export type ProducerRole = 'producer' | 'producer_and_talent';

/** A role value as the api sends it, or null for anything else. */
export function producerRoleOf(v: unknown): ProducerRole | null {
  return v === 'producer' || v === 'producer_and_talent' ? v : null;
}

/** The badge text for a role: "Producer and talent" for both roles, else "Producer". */
export function producerBadgeText(role: ProducerRole | null | undefined): string {
  return role === 'producer_and_talent' ? PRODUCER_AND_TALENT_LABEL : PRODUCER_LABEL;
}

/** A post's `authorIsProducer` (PostDto.AuthorIsProducer), true only when the api says so. */
export function authorIsProducerOf(p: unknown): boolean {
  const o = p as { authorIsProducer?: unknown; AuthorIsProducer?: unknown } | null | undefined;
  return o?.authorIsProducer === true || o?.AuthorIsProducer === true;
}

/** A post's `authorProducerRole`, null unless the label is on. */
export function authorProducerRoleOf(p: unknown): ProducerRole | null {
  const o = p as { authorProducerRole?: unknown; AuthorProducerRole?: unknown } | null | undefined;
  return producerRoleOf(o?.authorProducerRole ?? o?.AuthorProducerRole);
}

/**
 * The public person read's label flag. The api exposes `isProducerLabel` (computed by the label
 * rule); a read that has no such field falls back to `isProducer`, which the api then computes by
 * the same rule.
 */
export function personIsProducerLabelOf(j: unknown): boolean {
  const o = j as { isProducerLabel?: unknown; IsProducerLabel?: unknown; isProducer?: unknown; IsProducer?: unknown } | null | undefined;
  const label = o?.isProducerLabel ?? o?.IsProducerLabel;
  if (typeof label === 'boolean') return label;
  const v = o?.isProducer ?? o?.IsProducer;
  return v === true || v === 1;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim().length > 0 ? v : null);

/** The public producer details on the person read; every field null unless the label is on. */
export type PublicProducerDetails = {
  role: ProducerRole | null;
  businessName: string | null;
  website: string | null;
  produces: string | null;
  state: string | null;
};

export function publicProducerDetailsOf(j: unknown): PublicProducerDetails | null {
  if (!personIsProducerLabelOf(j)) return null;
  const o = (j ?? {}) as Record<string, unknown>;
  return {
    role: producerRoleOf(o.producerRole ?? o.ProducerRole),
    businessName: str(o.producerBusinessName ?? o.ProducerBusinessName),
    website: str(o.producerWebsite ?? o.ProducerWebsite),
    produces: str(o.producerProduces ?? o.ProducerProduces),
    state: str(o.producerState ?? o.ProducerState),
  };
}

/**
 * A talent or producer card's label, from the card DTO's own fields (isProducer, producerRole,
 * producerBusinessName). The label shows only when isProducer is true AND the role is a role
 * value: some person reads carry the raw People.IsProducer switch with no role, and those never
 * show a label.
 */
export type CardProducer = { role: ProducerRole; businessName: string | null };

export function cardProducerOf(p: unknown): CardProducer | null {
  const o = (p ?? {}) as Record<string, unknown>;
  const on = o.isProducer === true || o.IsProducer === true;
  const role = producerRoleOf(o.producerRole ?? o.ProducerRole);
  if (!on || !role) return null;
  return { role, businessName: str(o.producerBusinessName ?? o.ProducerBusinessName) };
}

/** The link text for a producer's website: the address's host, or the address as typed. */
export function websiteLinkText(url: string): string {
  try {
    const u = new URL(url);
    return u.host || url;
  } catch {
    return url;
  }
}

/** True for an absolute http or https address (the only kind the api saves). */
export function isSafeWebsite(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    const u = new URL(url);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname;
  } catch {
    return false;
  }
}
